import * as React from "react";
import type { CalendarGridProps } from "../types";
import {
  CalendarDate,
  today,
  getLocalTimeZone,
  parseCalendarDate,
  formatCalendarDate,
} from "../utils/calendarDateTime";
import "./CalendarGrid.css";

// =============================================================================
// CalendarGrid Component
// Month calendar display with date selection and range support
// =============================================================================

const WEEKDAY_HEADERS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

export const CalendarGrid = React.memo(function CalendarGrid({
  year,
  month,
  selectedDate,
  rangeStart,
  rangeEnd,
  hoveredDate,
  editingField,
  minDate,
  maxDate,
  onDateClick,
  onDateHover,
  showOutsideDays = true,
  timezone = "UTC",
  actionTooltip,
  className,
}: CalendarGridProps) {
  // ---------------------------------------------------------------------------
  // Computed Values
  // ---------------------------------------------------------------------------

  const weeks = React.useMemo(() => {
    return generateCalendarWeeks(year, month, showOutsideDays);
  }, [year, month, showOutsideDays]);

  const todayDate = React.useMemo(() => {
    // Use library's today() with appropriate timezone
    return today(timezone === "local" ? getLocalTimeZone() : "UTC");
  }, [timezone]);

  // Parse date constraints to CalendarDate
  const minDateCd = React.useMemo(() => minDate ? parseCalendarDate(minDate) : null, [minDate]);
  const maxDateCd = React.useMemo(() => maxDate ? parseCalendarDate(maxDate) : null, [maxDate]);

  // Parse range dates to CalendarDate
  const rangeStartCd = React.useMemo(() => rangeStart ? parseCalendarDate(rangeStart) : null, [rangeStart]);
  const rangeEndCd = React.useMemo(() => rangeEnd ? parseCalendarDate(rangeEnd) : null, [rangeEnd]);
  const hoveredDateCd = React.useMemo(() => hoveredDate ? parseCalendarDate(hoveredDate) : null, [hoveredDate]);

  // ---------------------------------------------------------------------------
  // Event Handlers
  // ---------------------------------------------------------------------------

  const handleDateClick = React.useCallback(
    (dateStr: string, isDisabled: boolean) => {
      if (isDisabled || !onDateClick) return;
      onDateClick(dateStr);
    },
    [onDateClick]
  );

  const handleDateHover = React.useCallback(
    (dateStr: string | null, isDisabled: boolean) => {
      if (isDisabled || !onDateHover) return;
      onDateHover(dateStr);
    },
    [onDateHover]
  );

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent, dateStr: string, isDisabled: boolean) => {
      if (isDisabled) return;

      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onDateClick?.(dateStr);
      }
    },
    [onDateClick]
  );

  // ---------------------------------------------------------------------------
  // Render Helpers
  // ---------------------------------------------------------------------------

  const getDayClasses = (day: CalendarDay): string => {
    const classes = ["calendar-grid__day"];

    if (day.isOutside) {
      classes.push("calendar-grid__day--outside");
    }

    // Disabled state
    const isDisabled = isDayDisabled(day.calendarDate, minDateCd, maxDateCd);
    if (isDisabled) {
      classes.push("calendar-grid__day--disabled");
    }

    // Today - use CalendarDate comparison
    if (isSameCalendarDate(day.calendarDate, todayDate) && !day.isOutside) {
      classes.push("calendar-grid__day--today");
    }

    // Selected date (for single date picker)
    if (selectedDate && day.dateStr === selectedDate && !day.isOutside) {
      classes.push("calendar-grid__day--selected");
    }

    // Range handling
    if (rangeStartCd && rangeEndCd) {
      const isRangeStart = isSameCalendarDate(day.calendarDate, rangeStartCd);
      const isRangeEnd = isSameCalendarDate(day.calendarDate, rangeEndCd);

      // Check if we're hovering to change the range
      const isHoveringToChange = hoveredDateCd && editingField;

      // Check if hover would create an invalid range
      const wouldBeInvalidRange =
        isHoveringToChange &&
        hoveredDateCd &&
        ((editingField === "from" && hoveredDateCd.compare(rangeEndCd) > 0) ||
          (editingField === "to" && hoveredDateCd.compare(rangeStartCd) < 0));

      if (isHoveringToChange && !wouldBeInvalidRange) {
        // Calculate the preview range based on which field is being edited
        const previewStart = editingField === "from" ? hoveredDateCd! : rangeStartCd;
        const previewEnd = editingField === "from" ? rangeEndCd : hoveredDateCd!;

        const isPreviewStart = isSameCalendarDate(day.calendarDate, previewStart);
        const isPreviewEnd = isSameCalendarDate(day.calendarDate, previewEnd);
        const isInPreview =
          day.calendarDate.compare(previewStart) > 0 && day.calendarDate.compare(previewEnd) < 0;

        // Show hover preview range
        if (isPreviewStart) {
          classes.push("calendar-grid__day--hover-range-start");
        }
        if (isPreviewEnd) {
          classes.push("calendar-grid__day--hover-range-end");
        }
        if (isInPreview) {
          classes.push("calendar-grid__day--hover-preview");
        }

        // Show original range as muted (only parts not covered by preview)
        const isInOriginal =
          day.calendarDate.compare(rangeStartCd) >= 0 && day.calendarDate.compare(rangeEndCd) <= 0;
        const isInNewPreview =
          day.calendarDate.compare(previewStart) >= 0 && day.calendarDate.compare(previewEnd) <= 0;
        if (isInOriginal && !isInNewPreview) {
          classes.push("calendar-grid__day--muted-range");
        }
      } else {
        // No hover - show normal range
        if (isRangeStart) {
          classes.push("calendar-grid__day--range-start");
        }
        if (isRangeEnd) {
          classes.push("calendar-grid__day--range-end");
        }
        if (
          !isRangeStart &&
          !isRangeEnd &&
          day.calendarDate.compare(rangeStartCd) > 0 &&
          day.calendarDate.compare(rangeEndCd) < 0
        ) {
          classes.push("calendar-grid__day--in-range");
        }
      }
    } else if (rangeStartCd && !rangeEndCd) {
      // Only start is selected - show it, and optionally show hover preview
      const isRangeStart = isSameCalendarDate(day.calendarDate, rangeStartCd);

      // Always show start as range-start
      if (isRangeStart) {
        classes.push("calendar-grid__day--range-start");
      }

      // Add hover preview if hovering (only if hoverDate >= startDate)
      if (hoveredDateCd && hoveredDateCd.compare(rangeStartCd) >= 0) {
        const isHovered = isSameCalendarDate(day.calendarDate, hoveredDateCd);

        // Show hovered as range-end (preview)
        if (isHovered && !isRangeStart) {
          classes.push("calendar-grid__day--hover-range-end");
        }

        // Show in-between dates
        if (
          day.calendarDate.compare(rangeStartCd) > 0 &&
          day.calendarDate.compare(hoveredDateCd) < 0
        ) {
          classes.push("calendar-grid__day--hover-preview");
        }
      }
    } else if (!rangeStartCd && rangeEndCd) {
      // Only end is selected - show it, and optionally show hover preview
      const isRangeEnd = isSameCalendarDate(day.calendarDate, rangeEndCd);

      // Always show end as range-end
      if (isRangeEnd) {
        classes.push("calendar-grid__day--range-end");
      }

      // Add hover preview if hovering (only if hoverDate <= endDate)
      if (hoveredDateCd && hoveredDateCd.compare(rangeEndCd) <= 0) {
        const isHovered = isSameCalendarDate(day.calendarDate, hoveredDateCd);

        // Show hovered as range-start (preview)
        if (isHovered && !isRangeEnd) {
          classes.push("calendar-grid__day--hover-range-start");
        }

        // Show in-between dates
        if (
          day.calendarDate.compare(hoveredDateCd) > 0 &&
          day.calendarDate.compare(rangeEndCd) < 0
        ) {
          classes.push("calendar-grid__day--hover-preview");
        }
      }
    }

    return classes.join(" ");
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className={`calendar-grid ${className || ""}`} role="grid">
      {/* Weekday Headers */}
      <div className="calendar-grid__header" role="row">
        {WEEKDAY_HEADERS.map((day) => (
          <div key={day} className="calendar-grid__header-cell" role="columnheader">
            {day}
          </div>
        ))}
      </div>

      {/* Week Rows */}
      {weeks.map((week, weekIndex) => (
        <div key={weekIndex} className="calendar-grid__row" role="row">
          {week.map((day, dayIndex) => {
            if (!day) {
              return (
                <div
                  key={`empty-${dayIndex}`}
                  className="calendar-grid__day calendar-grid__day--empty"
                  role="gridcell"
                />
              );
            }

            const isDisabled = isDayDisabled(day.calendarDate, minDateCd, maxDateCd);
            const isSelected = selectedDate === day.dateStr && !day.isOutside;

            return (
              <div
                key={day.dateStr}
                className={getDayClasses(day)}
                role="gridcell"
                aria-selected={isSelected}
                aria-disabled={isDisabled}
                tabIndex={isDisabled ? -1 : 0}
                title={!isDisabled && !day.isOutside && actionTooltip ? actionTooltip : undefined}
                onClick={() => handleDateClick(day.dateStr, isDisabled)}
                onMouseEnter={() => handleDateHover(day.dateStr, isDisabled)}
                onMouseLeave={() => handleDateHover(null, false)}
                onKeyDown={(e) => handleKeyDown(e, day.dateStr, isDisabled)}
              >
                <span className="calendar-grid__day-number">{day.calendarDate.day}</span>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
});

// =============================================================================
// Helper Types
// =============================================================================

interface CalendarDay {
  calendarDate: CalendarDate;
  dateStr: string;
  isOutside: boolean;
}

// =============================================================================
// Helper Functions
// =============================================================================

function generateCalendarWeeks(year: number, month: number, showOutsideDays: boolean): (CalendarDay | null)[][] {
  const weeks: (CalendarDay | null)[][] = [];

  // Get first day of month using CalendarDate
  const firstDayOfMonth = new CalendarDate(year, month, 1);

  // Get days in current month
  const daysInMonth = getDaysInMonth(year, month);

  // Get day of week (library: 0 = Sunday, need Monday = 0)
  // Use Date for day of week calculation since CalendarDate doesn't have it directly
  const firstDayDate = new Date(year, month - 1, 1);
  let startDayOfWeek = firstDayDate.getDay() - 1;
  if (startDayOfWeek < 0) startDayOfWeek = 6; // Sunday becomes 6

  // Get previous month info
  const prevMonth = month === 1 ? 12 : month - 1;
  const prevYear = month === 1 ? year - 1 : year;
  const daysInPrevMonth = getDaysInMonth(prevYear, prevMonth);

  // Get next month info
  const nextMonth = month === 12 ? 1 : month + 1;
  const nextYear = month === 12 ? year + 1 : year;

  let currentDay = 1;
  let nextMonthDay = 1;

  // Generate 6 weeks
  for (let week = 0; week < 6; week++) {
    const weekDays: (CalendarDay | null)[] = [];

    for (let dayOfWeek = 0; dayOfWeek < 7; dayOfWeek++) {
      if (week === 0 && dayOfWeek < startDayOfWeek) {
        // Previous month days
        if (showOutsideDays) {
          const day = daysInPrevMonth - startDayOfWeek + dayOfWeek + 1;
          const calendarDate = new CalendarDate(prevYear, prevMonth, day);
          weekDays.push({
            calendarDate,
            dateStr: formatCalendarDate(calendarDate),
            isOutside: true,
          });
        } else {
          weekDays.push(null);
        }
      } else if (currentDay > daysInMonth) {
        // Next month days
        if (showOutsideDays) {
          const calendarDate = new CalendarDate(nextYear, nextMonth, nextMonthDay);
          weekDays.push({
            calendarDate,
            dateStr: formatCalendarDate(calendarDate),
            isOutside: true,
          });
          nextMonthDay++;
        } else {
          weekDays.push(null);
        }
      } else {
        // Current month days
        const calendarDate = new CalendarDate(year, month, currentDay);
        weekDays.push({
          calendarDate,
          dateStr: formatCalendarDate(calendarDate),
          isOutside: false,
        });
        currentDay++;
      }
    }

    weeks.push(weekDays);

    // Stop if we've shown all days and it's not the first week after the month ends
    if (currentDay > daysInMonth && week >= 3) {
      // Check if next row would be all outside days
      const remainingDays = 7 - ((startDayOfWeek + daysInMonth) % 7);
      if (remainingDays === 7 || !showOutsideDays) {
        break;
      }
    }
  }

  return weeks;
}

function getDaysInMonth(year: number, month: number): number {
  // Create last day of month using Date (day 0 of next month = last day of current month)
  return new Date(year, month, 0).getDate();
}

function isSameCalendarDate(a: CalendarDate, b: CalendarDate): boolean {
  return a.compare(b) === 0;
}

function isDayDisabled(
  day: CalendarDate,
  minDate: CalendarDate | null,
  maxDate: CalendarDate | null
): boolean {
  if (minDate && day.compare(minDate) < 0) return true;
  if (maxDate && day.compare(maxDate) > 0) return true;
  return false;
}
